import React, { useState, useEffect, useReducer } from 'react';
import { useForm } from 'react-hook-form';
import { get, uniqueId } from 'lodash';
import axios from 'axios';
import {
	SdfAlert,
	SdfBusyIndicator,
	SdfButton,
	SdfFocusPane,
	SdfIcon,
	SdfWizardLayout,
	SdfWizardStepsVertical,
	SdfWizardStepsVerticalItem
} from '@synerg/react-components';
import StepOne from './steps/stepOne';
import StepTwo from './steps/stepTwo';
import StepThree from './steps/stepThree';
import StepFour from './steps/stepFour';
import StepFive from './steps/stepFive';

const defaultValues = {
	projectName: '',
	projectDescription: '',
	companySize: null,
	useCaseID: null,
	useCaseName: null,
	appStore: null,
	productIntegration: null
};

function formReducer(state, action) {
	switch (action.type) {
		case 'UPDATE':
			return {
				...state,
				...action.payload
			};
		default:
			return state;
	}
}

export const ProjectSteps = (props) => {
	const { hideModal, isDataCloudProject } = props || {};
	const [useCases, setUseCases] = useState([]);
	const [products, setProducts] = useState([]);

	const [loading, setLoading] = useState(false);

	const [applicationNameList, setApplicationNameList] = useState(undefined);
	const [error, setError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const { control, reset, formState } = useForm({ mode: 'onChange', defaultValues });

	const [state, dispatch] = useReducer(formReducer, defaultValues);
	const [activeStep, setActiveStep] = useState(0);
	const [validForm, setValidForm] = useState(true);

	const [selectedRadioBtnIndex, setSelectedRadioBtnIndex] = useState(null);
	const [selectedProductBtnIndex, setSelectedProductBtnIndex] = useState(null);
	const [selectedUseCaseBtnIndex, setSelectedUseCaseBtnIndex] = useState(null);
	const [showDataEraseWarning, setDataEraseWarning] = useState(false);

	const backClickHandler = () => {
		if (activeStep !== 0) {
			setActiveStep(activeStep - 1);
		}
		// if (activeStep === 1) {
		// 	setSelectedRadioBtnIndex(null);
		// 	setSelectedProductBtnIndex(null);
		// 	setSelectedUseCaseBtnIndex(null);
		// }
		// if (activeStep === 2) {
		// 	setSelectedProductBtnIndex(null);
		// 	setSelectedUseCaseBtnIndex(null);
		// }
		// if (activeStep === 3) {
		// 	setSelectedUseCaseBtnIndex(null);
		// }
	};

	const nextClickHandler = () => {
		if (canGoNext()) {
			setActiveStep(activeStep + 1);
		}
	};

	const cancelClickHandler = () => {
		const { projectName, projectDescription } = state;
		if(projectName || projectDescription) {
			setDataEraseWarning(true)
		} else {
			reset(defaultValues);
			hideModal(false)
		}
	}

	const continueBtnClickHandler = () => {
		reset(defaultValues);
		hideModal(false)
		setDataEraseWarning(false);
	}

	function canGoNext() {
		switch (activeStep) {
			case 0:
				return validForm && !error;

			case 1:
				if (selectedRadioBtnIndex === 0 || selectedRadioBtnIndex === 1) {
					return true;
				}
				break;

			case 2:
				if (selectedProductBtnIndex !== null) {
					return true;
				}
				break;

			case 3:
				if (selectedUseCaseBtnIndex !== null) {
					return true;
				}
				break;

			default:
				return false;
		}
	}

	useEffect(() => {
		if (formState.isValid) {
			setValidForm(true);
		} else {
			setValidForm(false);
		}
	}, [formState.isValid]);

	useEffect(() => {
	}, [state])

	const createProject = async () => {
		setLoading(true);
		try {
			const {data} = await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/project`, state);
			if (data._id) {
				hideModal(false);
			} else {
				setError(true);
				setErrorMessage('Unable to create project. Please try again');
			}
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to create project. Please try again.');
			setErrorMessage(errorMessage);
			setError(true);
		}
		setLoading(false);
	};

	useEffect(() => {
		async function getUseCases() {
			setLoading(true);
			try {
				Promise.all([
					axios(`${import.meta.env.VITE_ROOT_PATH}/api/organization/applications/name`),
					axios(`${import.meta.env.VITE_ROOT_PATH}/api/project/use-cases`),
					axios(`${import.meta.env.VITE_ROOT_PATH}/api/products`,
						{ params: { includeDataCloud:  isDataCloudProject } })])
					.then(([{ data:appNameList, status: appNameStatus },
							   { data: useCases, status: useCasesStatus },
							   { data: products, status: productsStatus }]) => {
						appNameStatus === 200 ? setApplicationNameList(appNameList) : setErrorMessage('Something went wrong when trying to get application name list. Please try again.')
						useCasesStatus === 200 ? setUseCases(useCases) : setErrorMessage('Something went wrong when trying to get use-cases. Please try again.')
						productsStatus === 200 ? setProducts(products) : setErrorMessage('Something went wrong when trying to get product list. Please try again.')
						if (appNameStatus !== 200 || useCasesStatus !== 200 ||  productsStatus !== 200) {
							setError(true);
						}
						setLoading(false);
					}).catch((e) => {
					setLoading(false);
					setErrorMessage(e.message);
					setError(true);
				});
			} catch (e) {
				setLoading(false);
				setError(true);
			}
		}
		getUseCases();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const ShowError = ({ message }) => {
		return (
			<SdfAlert
				key={uniqueId()}
				status={'error'}
				showToast={true}>
				<span slot="title"> Error </span>
				<div>{message || 'Something went wrong, please try again later'}</div>
			</SdfAlert>
		);
	};

	return (
		<div>
			<div>
				{loading && <SdfBusyIndicator style={{ color: "#1a8099" }} size="xl" fullScreen={true} />}
				<SdfWizardLayout>
					{!error && <div slot="page-content" className="px-8 hxl overflow-y-scroll">
						{/* Content of each step */}
						{activeStep === 0 && <StepOne state={state} dispatch={dispatch} control={control}
													  setError={setError} applicationNameList={applicationNameList} loadingAppNames={loading} />}
						{activeStep === 1 && <StepTwo state={state} dispatch={dispatch} control={control} selectedRadioBtnIndex={selectedRadioBtnIndex}
													  setSelectedRadioBtnIndex={setSelectedRadioBtnIndex} />}
						{activeStep === 2 && <StepThree state={state} dispatch={dispatch} control={control} products={products} selectedProductBtnIndex={selectedProductBtnIndex}
														setSelectedProductBtnIndex={setSelectedProductBtnIndex} />}
						{activeStep === 3 && <StepFour state={state} dispatch={dispatch} control={control} useCases={useCases} selectedUseCaseBtnIndex={selectedUseCaseBtnIndex}
													   setSelectedUseCaseBtnIndex={setSelectedUseCaseBtnIndex} />}
						{activeStep === 4 && <StepFive state={state} dispatch={dispatch} />}

					</div>}
					{error && <div slot="page-content" className="min-h-md px-8 min-w-fit">
						<ShowError message={errorMessage} />
					</div>}
					{/*Steps for Wizard*/}
					<SdfWizardStepsVertical slot="wizard-steps" class="flex-grow-0 w-64" style={{ marginTop: "20px" }}>
						<SdfWizardStepsVerticalItem class="w-48" step-status="visited" is-current={activeStep === 0}>Project details</SdfWizardStepsVerticalItem>

						{activeStep <= 1 && <SdfWizardStepsVerticalItem class="w-64" is-current={activeStep === 1}>Marketplace integration</SdfWizardStepsVerticalItem>}
						{activeStep > 1 && <SdfWizardStepsVerticalItem class="w-64" step-status="visited" is-current={activeStep === 1}>Marketplace integration</SdfWizardStepsVerticalItem>}

						{activeStep <= 2 && <SdfWizardStepsVerticalItem class="w-64" is-current={activeStep === 2}>Product integration</SdfWizardStepsVerticalItem>}
						{activeStep > 2 && <SdfWizardStepsVerticalItem class="w-64" step-status="visited" is-current={activeStep === 2}>Product integration</SdfWizardStepsVerticalItem>}

						{activeStep <= 3 && <SdfWizardStepsVerticalItem class="w-64" is-current={activeStep === 3}>Use case selection</SdfWizardStepsVerticalItem>}
						{activeStep > 3 && <SdfWizardStepsVerticalItem class="w-64" step-status="visited" is-current={activeStep === 3}>Use case selection</SdfWizardStepsVerticalItem>}

						{activeStep <= 4 && <SdfWizardStepsVerticalItem class="w-64" is-current={activeStep === 4}>Review</SdfWizardStepsVerticalItem>}
						{activeStep > 4 && <SdfWizardStepsVerticalItem class="w-64" step-status="visited" is-current={activeStep === 4}>Review</SdfWizardStepsVerticalItem>}
					</SdfWizardStepsVertical>
					{/* Buttons */}
					<SdfButton slot="buttons" emphasis="tertiary" variant="contained" onClick={cancelClickHandler}>Cancel</SdfButton>
					{activeStep > 0 && <SdfButton slot="buttons" emphasis="secondary" variant="contained" onClick={backClickHandler}><SdfIcon icon="nav-move-left" /> Back</SdfButton>}
					{canGoNext() && <SdfButton slot="buttons" emphasis="primary" variant="contained" iconPlacement='after' onClick={nextClickHandler}>Next <SdfIcon icon="nav-move-right" /></SdfButton>}
					{!canGoNext() && activeStep !== 4 && <SdfButton slot="buttons" disabled={true} emphasis="primary" variant="contained" iconPlacement='after' onClick={nextClickHandler}>Next <SdfIcon icon="nav-move-right" /></SdfButton>}
					{activeStep === 4 && <SdfButton slot="buttons" emphasis="primary" variant="contained" iconPlacement='after' onClick={createProject}>Generate</SdfButton>}
				</SdfWizardLayout>
				<SdfFocusPane
					spacing="loose"
					heading="Are you sure?"
					visible={showDataEraseWarning}
					size="sm"
					closeable={true}
        			dismissOnClickOutside={true}
					hideDismissButton={true}
					hideAcceptButton={true}
					urgencyType={'alert'}
					onSdfDismiss={() => {
						setDataEraseWarning(false);
					}}
				>
					<div className="w-full">
						<div className="text-body-02 mb-6">Are you sure you wish to exit the wizard? All of your entries will not be saved, and a new project will not be created.</div>
					</div>

					<SdfButton
						slot="custom-buttons"
						emphasis="primary"
						onClick={continueBtnClickHandler}
					>
						Yes, continue
					</SdfButton>
					<SdfButton
						slot="custom-buttons"
						onClick={() => setDataEraseWarning(false)}
					>
						No, go back
					</SdfButton>
				</SdfFocusPane>
			</div>
		</div>
	)
}
